import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { Observable, of, Subject } from 'rxjs';
import { catchError, map, switchMap, take } from 'rxjs/operators';

import { CmsPageSection, PageTemplate } from '../../../../models/content';
import { ModalService } from '../../../../services/modal.service';
import { suburbPageRequest } from '../../block-preview/block-preview-request';
import { SectionsEditorSource } from '../../block-preview/block-preview-source';
import {
  COMPONENT_OPTIONS,
  ContentSectionsEditorComponent,
} from '../../content-sections-editor.component';
import { blockKindLabel, OutlineRow, outlineFromServer } from '../page/page-structure';
import { apiErrorBody, publishNotSetUp } from '../workspace/api-errors';
import { PageTemplatesService } from '../workspace/page-templates.service';
import { StagePreviewService } from '../workspace/stage-preview.service';
import { insertToken, TemplateKey } from './suburb-template';
import { editableBlocks, removedBlocks, templateForSave } from './template-blocks';

/** How the editor closed: saved (and published, or why the publish didn't start), or not. */
export interface TemplateEditorResult {
  saved: boolean;
  published?: boolean;
  /** Saved, but the publish didn't start: 'not-set-up' (no website publishing here) or 'failed'. */
  notShipped?: 'not-set-up' | 'failed';
}

/**
 * The Suburb or Sub-region page template's editor (#262), in the side panel: its name, title and description
 * patterns, its blocks (the CMS block editor in template mode: headings, links and data blocks with placeholders, no
 * paragraphs), a preview of one page built from the saved template, Save and Publish. A Developer creates the
 * templates from the defaults while none exists. Mounted by PageEditorService.editTemplate; the reads and writes are
 * PageTemplatesService, the save's shape templateForSave. The stage previews this draft on the "Preview as" page
 * (#263): the active block on its own, or the whole page when none is.
 */
@Component({
  selector: 'app-page-template-editor',
  templateUrl: './page-template-editor.component.html',
  styleUrls: ['./page-template-editor.component.scss'],
})
export class PageTemplateEditorComponent implements OnInit, OnDestroy {
  key!: TemplateKey;
  stored: PageTemplate | null = null;
  name = '';
  title = '';
  description = '';
  saving = false;
  previewOptions: Array<{ label: string; value: string }> = [];
  previewId: string | null = null;
  previewRows: OutlineRow[] = [];
  previewError = '';

  private stagePreview!: SectionsEditorSource;
  private readonly previewPicked$ = new Subject<void>();

  /** Always rendered (hidden until there is a template), so it is there from the start. */
  @ViewChild(ContentSectionsEditorComponent, { static: true })
  editor!: ContentSectionsEditorComponent;

  constructor(
    public ref: DynamicDialogRef,
    public config: DynamicDialogConfig,
    public templates: PageTemplatesService,
    private modalService: ModalService,
    private stage: StagePreviewService
  ) {}

  ngOnInit(): void {
    this.key = this.config.data.key;
    this.previewOptions = this.templates.previewOptions(this.key);
    this.load();
    this.stagePreview = new SectionsEditorSource(
      this.editor,
      (sections, active) => suburbPageRequest(this.previewId, null, this.draft(sections), active),
      `Pick a ${this.kindName} in "Preview as" to see this template on it.`,
      this.previewPicked$
    );
    this.stage.show(this.stagePreview);
  }

  ngOnDestroy(): void {
    this.stage.release(this.stagePreview);
    this.previewPicked$.complete();
  }

  get kindName(): string {
    return this.key === 'subregion' ? 'sub-region' : 'suburb';
  }

  private load(): void {
    this.stored = this.templates.template(this.key);
    this.name = this.stored?.name ?? '';
    this.title = this.stored?.title ?? '';
    this.description = this.stored?.description ?? '';
    this.editor.initFrom(editableBlocks(this.stored));
  }

  createFromDefaults(): void {
    this.saving = true;
    this.templates.createFromDefaults().subscribe({
      next: () => {
        this.saving = false;
        this.load();
      },
      error: (err: unknown) => this.failed(err),
    });
  }

  /** A placeholder from the menu into the title or description pattern, at the caret. */
  addToken(
    field: 'title' | 'description',
    token: string,
    input: HTMLInputElement | HTMLTextAreaElement
  ): void {
    this[field] = insertToken(this[field], token, input.selectionStart, input.selectionEnd).text;
  }

  preview(recordId: string | null): void {
    this.previewRows = [];
    this.previewError = '';
    this.previewPicked$.next();
    if (!recordId) return;
    this.templates.preview(recordId).subscribe({
      next: (d) =>
        (this.previewRows = outlineFromServer(d.blocks, (b) =>
          blockKindLabel(b, COMPONENT_OPTIONS)
        )),
      error: () => (this.previewError = "The preview couldn't load."),
    });
  }

  save(publish: boolean): void {
    const editor = this.editor;
    if (!this.stored) return;
    const problem =
      !this.name.trim() || !this.title.trim()
        ? 'The template needs a name and a title pattern.'
        : editor.validate(true);
    if (problem) return this.problem(problem);
    const fields = { name: this.name, title: this.title, description: this.description };
    const sections = editor.toMap();
    const anchored = this.templates.anchoredTo(this.key, removedBlocks(this.stored, sections));
    const steps = [
      anchored.records
        ? this.ask(
            'Remove blocks from the template?',
            `${anchored.records} ${this.kindName} page${anchored.records === 1 ? ' has' : 's have'} blocks placed after what you removed; they stay where it was.`,
            'Remove and save'
          )
        : of(true),
      publish ? this.confirmPublish() : of(true),
    ];
    steps[0]
      .pipe(switchMap((ok) => (ok ? steps[1] : of(false))))
      .subscribe((ok) => ok && this.write(fields, sections, publish));
  }

  private write(
    fields: { name: string; title: string; description: string },
    sections: Record<string, CmsPageSection>,
    publish: boolean
  ): void {
    this.saving = true;
    this.templates
      .save(this.key, this.stored, fields, sections)
      .pipe(switchMap(() => (publish ? this.shipped() : of<TemplateEditorResult>({ saved: true }))))
      .subscribe({
        next: (result) => this.ref.close(result),
        error: (err: unknown) => this.failed(err),
      });
  }

  /** The publish after a save: saved either way, so a refused publish is reported, not thrown. */
  private shipped(): Observable<TemplateEditorResult> {
    return this.templates.publish(this.key).pipe(
      map((): TemplateEditorResult => ({ saved: true, published: true })),
      catchError((err: unknown) =>
        of<TemplateEditorResult>({
          saved: true,
          notShipped: publishNotSetUp(err) ? 'not-set-up' : 'failed',
        })
      )
    );
  }

  /** "This changes every published suburb page: N", with the server's count. */
  private confirmPublish(): Observable<boolean> {
    return this.templates
      .publishedPages(this.key)
      .pipe(
        switchMap((n) =>
          this.ask(
            'Publish the template?',
            `This changes every published ${this.kindName} page: ${n}. They go to the website preview together.`,
            'Save and publish'
          )
        )
      );
  }

  onCancel(): void {
    this.ref.close({ saved: false } as TemplateEditorResult);
  }

  /** The template as Save would write it, for the stage's preview (none before the template exists). */
  private draft(sections: Record<string, CmsPageSection>): PageTemplate | null {
    if (!this.stored) return null;
    const fields = { name: this.name, title: this.title, description: this.description };
    const stored = { opened: this.stored, current: this.stored };
    return templateForSave(this.key, stored, fields, sections).template;
  }

  private ask(title: string, copy: string, label: string): Observable<boolean> {
    const modal = this.modalService.generic({
      title,
      copy: [copy],
      buttons: [
        { label: 'Cancel', key: 'cancel', class: 'p-button-text' },
        { label, key: 'confirm', class: '' },
      ],
    });
    return modal.onClose.pipe(
      take(1),
      map((key: string) => key === 'confirm')
    );
  }

  private failed(err: unknown): void {
    this.saving = false;
    this.modalService.error(apiErrorBody(err));
  }

  private problem(text: string): void {
    this.modalService.generic({
      title: "The template can't be saved yet",
      copy: [text],
      buttons: [{ label: 'Close', key: 'close', class: 'p-button-text' }],
    });
  }
}
